import React, { useEffect, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowUpRightIcon, GithubIcon, XIcon } from 'lucide-react';
import { ExperimentItem } from '../lib/cmsTypes';
interface ExperimentModalProps {
  experiment: ExperimentItem;
  onClose: () => void;
}
const sectionVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: 0.15 + i * 0.08,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
function ContentRow({
  label,
  children,
  index
}: {
  label: string;
  children: React.ReactNode;
  index: number;
}) {
  if (!children) return null;
  return <motion.div custom={index} variants={sectionVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 md:gap-16 py-10 md:py-14" data-id="element-128">
      <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium pt-1" data-id="element-129">
        {label}
      </h3>
      <div className="font-mono text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl whitespace-pre-line" data-id="element-130">
        {children}
      </div>
    </motion.div>;
}
export function ExperimentModal({
  experiment,
  onClose
}: ExperimentModalProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);
  const displayImages = experiment.gallery?.length > 0 ? experiment.gallery.map(g => g.url) : experiment.coverImage ? [experiment.coverImage] : [];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="fixed inset-0 z-50 bg-background overflow-y-auto" data-id="element-131">
      {/* Close button */}
      <button onClick={onClose} className="fixed top-6 right-6 md:top-8 md:right-12 z-50 p-2 text-muted-foreground hover:text-foreground transition-colors duration-200" aria-label="Close" data-id="element-132">
        <XIcon className="w-6 h-6" data-id="element-133" />
      </button>

      <div className="min-h-screen flex flex-col" data-id="element-134">
        {/* Main content area */}
        <div className="flex-1 w-full max-w-[960px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-8" data-id="element-135">
          {/* Breadcrumb */}
          <motion.div custom={0} variants={sectionVariants} initial="hidden" animate="visible" className="flex items-center gap-2 mb-8 md:mb-10" data-id="element-136">
            <span className="font-mono text-xs text-primary tracking-[0.2em] uppercase" data-id="element-137">
              // Lab Experiment
            </span>
            <span className="font-mono text-xs text-muted-foreground" data-id="element-138">
              {'>'}
            </span>
            <span className="font-mono text-xs text-muted-foreground tracking-widest uppercase" data-id="element-139">
              {experiment.type || 'Exploration'}
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1 custom={1} variants={sectionVariants} initial="hidden" animate="visible" className="font-display text-[clamp(2.5rem,8vw,7rem)] font-bold leading-[0.9] tracking-tighter mb-8 md:mb-10 text-foreground" data-id="element-140">
            {experiment.title}
          </motion.h1>

          {/* Tags */}
          <motion.div custom={2} variants={sectionVariants} initial="hidden" animate="visible" className="flex flex-wrap gap-3 mb-16 md:mb-20" data-id="element-141">
            {experiment.type && <span className="font-mono text-xs px-4 py-2 border border-border text-foreground/80" data-id="element-142">
                {experiment.type}
              </span>}
            {experiment.tools?.length > 0 && <span className="font-mono text-xs px-4 py-2 border border-border text-foreground/80" data-id="element-143">
                {experiment.tools.join(', ')}
              </span>}
            {experiment.tags?.map(tag => <span key={tag} className="font-mono text-xs px-4 py-2 border border-border text-muted-foreground" data-id="element-144">
                {tag}
              </span>)}
          </motion.div>

          {/* Overview */}
          <ContentRow label="Overview" index={3} data-id="element-145">
            {experiment.longDescription || experiment.shortDescription}
          </ContentRow>

          <div className="border-t border-border" data-id="element-146" />

          {/* Concept / Process / Result */}
          <ContentRow label="Concept" index={4} data-id="element-147">
            {experiment.concept}
          </ContentRow>

          <ContentRow label="Process" index={5} data-id="element-148">
            {experiment.process}
          </ContentRow>

          <ContentRow label="Result" index={6} data-id="element-149">
            {experiment.result}
          </ContentRow>

          {experiment.notes && <ContentRow label="Notes" index={7} data-id="element-150">
              {experiment.notes}
            </ContentRow>}

          {/* Images section */}
          {displayImages.length > 0 && <>
              <div className="border-t border-border" data-id="element-151" />
              <motion.div custom={8} variants={sectionVariants} initial="hidden" animate="visible" className="py-12 md:py-16" data-id="element-152">
                <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium mb-8" data-id="element-153">
                  Visuals
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-154">
                  {displayImages.map((img, idx) => <div key={idx} className="overflow-hidden border border-border bg-card" data-id="element-155">
                      <img src={img} alt={`${experiment.title} visual ${idx + 1}`} className="w-full aspect-video object-cover" loading="lazy" data-id="element-156" />
                    </div>)}
                </div>
              </motion.div>
            </>}

          <div className="border-t border-border" data-id="element-157" />

          {/* Footer navigation */}
          <motion.div custom={9} variants={sectionVariants} initial="hidden" animate="visible" className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 py-8 md:py-10" data-id="element-158">
            <button onClick={onClose} className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-300" aria-label="Back to lab" data-id="element-159">
              <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" data-id="element-160" />
              Back to Lab
            </button>

            <div className="flex items-center gap-6" data-id="element-161">
              {experiment.sourceUrl && experiment.sourceUrl !== '#' && <a href={experiment.sourceUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-300" data-id="element-162">
                  Source Code
                  <ArrowUpRightIcon className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" data-id="element-163" />
                </a>}

              {experiment.liveDemoUrl && experiment.liveDemoUrl !== '#' && <a href={experiment.liveDemoUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-primary hover:text-foreground transition-colors duration-300" data-id="element-164">
                  Live Demo
                  <ArrowUpRightIcon className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" data-id="element-165" />
                </a>}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}